import {
  SdfBox,
  SdfButton,
  SdfExpandableBox,
  SdfIcon,
  SdfIconButton,
  SdfLink,
  SdfTooltip,
} from "@synerg/react-components";
import React, { useEffect, useState, useRef } from "react";
import { PageTitle } from "../../components/PageTitle";
import {
  adpIternationalProduct,
  adpNorthAmericaProduct,
} from "../../utility/articlesUtils";
import usCndIcon from "../../assets/images/us-cnd-icon.svg";
import usIcon from "../../assets/images/us-icon.svg";
import europeanGlobe from "../../assets/images/european-globe.png";
import useMediaQuery from "../../hooks/useMediaQuery";
import Breadcrumb from "../../components/Breadcrumb";

const breadcrumbPath = [
  { name: "Home", route: "/" },
  { name: "Support / Help Center" },
];

function HelpCenterPage() {
  const onHandleGetHelp = (href) => {
    let a = document.createElement("a");
    a.href = href;
    a.target = "_blank";
    a.click();
  };
  const [tooltipStyles, setTooltipStyles] = useState({
    currentWidth: "w-64",
    position: "bottom",
    padding: "p-1",
  });
  const tooltipRef = useRef(null); 
  const handleResize = () => {
    let updatedStyles = {
      currentWidth: "w-64",
      position: "bottom",
      padding: "p-1",
    };

    if (window.innerWidth <= 400) {
      updatedStyles = {
        currentWidth: "w-48 text-xs whitespace-normal",
        position: "top",
        padding: "p-0",
      };
    } else if (window.innerWidth <= 500) {
      updatedStyles = {
        currentWidth: "w-62 text-xs whitespace-normal",
        position: "bottom",
        padding: "p-1",
      };
    } else if (window.innerWidth <= 600) {
      updatedStyles = {
        currentWidth: "w-56 whitespace-normal",
        position: "auto-end",
        padding: "p-1",
      };
    }

    setTooltipStyles((prevStyles) =>
      JSON.stringify(prevStyles) !== JSON.stringify(updatedStyles)
        ? updatedStyles
        : prevStyles
    );
  };

  useEffect(() => {
    const resizeListener = () => handleResize();

    window.addEventListener("resize", resizeListener);

    return () => {
      window.removeEventListener("resize", resizeListener);
    };
  }, []);

  const isBetweenMiddleAndLargeScreen = useMediaQuery("(min-width: 1407px)");
  const isBelowMedium = useMediaQuery("(max-width: 920px)");
  
  const sections = [
    {
      title: "North American Products",
      products: adpNorthAmericaProduct,
      commonStyle: "",
      adpMarketplacePartners: {
        icon: usCndIcon,
        bgColor: "bg-alert-neutral-100",
        title: "ADP Marketplace Partners",
        cards: [
          {
            style: !isBetweenMiddleAndLargeScreen ? "lg:h-md" : "lg:h-xs",
            title: "Third-party partner developers",
            desc: (
              <>
                If you’re an ADP Marketplace partner integrating with a North
                American ADP product, submit a ticket and track its progress
                through the{" "}
                <SdfLink
                  external
                  href="https://partnersupport.adp.com/hc/en-us/requests"
                  target="_blank"
                >
                  Partner Help Center
                </SdfLink>
                . A member of our support team will help you resolve your issue.{" "}
              </>
            ),
            href: "https://partnersupport.adp.com/hc/en-us/requests",
          },
        ],
      },
      adpClients: {
        icon: usIcon,
        bgColor: "bg-alert-neutral-50",
        title: "ADP Clients",
        cards: [
          {
            style: !isBetweenMiddleAndLargeScreen
              ? "lg:h-md"
              : "lg:h-xs md:h-xs sm:h-xs",
            title: "ADP API Central users",
            desc: (
              <>
                If you’re an ADP client or other developer using ADP API Central
                for a North American product, log in to{" "}
                <SdfLink
                  external
                  href="https://api-central.adp.com/"
                  target="_blank"
                >
                  ADP API Central
                </SdfLink>{" "}
                to submit a ticket. Select the question mark icon{" "}
                <SdfIcon icon="action-help" /> in the upper-right corner of the
                application.
              </>
            ),
            bottomText: (
              <>
                Not already using ADP API Central as a North American ADP
                client?<br></br>
                <SdfLink
                  className="font-bold"
                  external
                  href="https://apps.adp.com/en-US/apps/410612"
                  target="_blank"
                >
                  Learn more
                </SdfLink>
              </>
            ),
            href: "https://api-central.adp.com/",
          },
          {
            style: !isBetweenMiddleAndLargeScreen
              ? "lg:h-md"
              : "lg:h-xs md:h-xs sm:h-xs",
            title: "All other ADP clients",
            desc: (
              <>
                If you’re an ADP client who purchased a North American product
                and are having trouble implementing ADP APIs and have not
                purchased ADP API Central, visit{" "}
                <SdfLink
                  className=""
                  external
                  href="https://www.adp.com/contact-us/customer-service.aspx"
                  target="_blank"
                >
                  ADP Customer Service & Support.
                </SdfLink>{" "}
              </>
            ),

            href: "https://www.adp.com/contact-us/customer-service.aspx",
          },
        ],
      },
    },
    {
      title: "International Products",
      products: adpIternationalProduct,
      commonStyle: isBetweenMiddleAndLargeScreen ? "h-md" : isBelowMedium ? "w-full" : "h-xl",
      adpMarketplacePartners: {
        icon: europeanGlobe,
        bgColor: "bg-alert-neutral-100",
        title: "ADP Marketplace Partners",
        cards: [
          {
            bgColor: "bg-alert-info-50",
            title: "Marketplace partner developers",
            desc: <>
            <div>If you're an existing ADP Marketplace partner integrating with an international ADP product, select the button below to file a ticket with ADP support. We’ll get back to you as soon as possible.</div>
            <div className="pt-3">ADP eService is a web portal to provide you as an ADP partner access to service information. The portal can be 
              used to submit new service requests and check the status of service requests already in progress. With ADP eService 
              you benefit from a timely and accurate response to your Service Requests and 24/7 access. The eService application is 
              only available for registered users. If you do not have access yet, please contact your partner manager to provide access. 
              In order to submit a ticket, start here.</div>
            </>,
             href: "https://serviceportal.adp.com",
             buttonText: "Submit a ticket",
          },
        ],
      },
      adpClients: {
        icon: europeanGlobe,
        bgColor: "bg-alert-neutral-50",
        title: "ADP Clients",
        cards: [
          {
            bgColor: "bg-alert-info-50",
            title: "ADP API Central",
            desc: <>
            <div>If you're an ADP client integrating with an international ADP product, select the button below to file a ticket with ADP eService. If you can’t access eService, contact your HR department.</div>
            <div className="pt-3">ADP eService is a web portal to provide you as an ADP client access to service information. The portal can be 
              used to submit new service requests and check the status of service requests already in progress. With ADP eService 
              you benefit from a timely and accurate response to your Service Requests and 24/7 access. The eService application is 
              only available for registered users. If you do not have access yet, please contact your HR department to provide access. 
              In order to submit a ticket, start here.</div>
            </>,
            href: "https://serviceportal.adp.com",
            buttonText: "Submit a ticket",
          },
        ],
      },
    },
  ];

  return (
    <div className="pb-12 px-4 md:pl-8 md:pr-0 mr-20">
      <Breadcrumb className="pl-4 md:pl-12 lg:pl-12" path={breadcrumbPath} />
      <PageTitle
        className="text-center sm:text-left text-heading-08 font-black mb-4"
        text="Help center"
      />
      <div className="font-black text-heading-06">
        Product Integration Support
      </div>
      <div className="text-body-02 pb-6 pt-3">
        Select the region where your ADP product is sold to be routed to the
        correct support channel.
      </div>

      {sections.map((section, index) => {
        return (
          <SdfExpandableBox
            key={index}
            className="mb-4"
            variant="bordered"
            triggerPlacement="after"
          >
            <div slot="header" className="flex text-heading-04 font-bold mb-1">
              <span className="mb-px">{section.title}</span>
              <SdfTooltip
                className="pl-2"
                tooltipId="alert-info"
                attachmentPoint={tooltipStyles.position}
              >
                <SdfIconButton slot="tooltip-target">
                  <SdfIcon icon="alert-info" className="mt-px"></SdfIcon>
                </SdfIconButton>
                <div className={tooltipStyles.currentWidth} ref={tooltipRef}>
                  <span>
                    {section.products.map((product, index) => (
                      <div key={index} className={tooltipStyles.padding}>
                        {product.label}
                      </div>
                    ))}
                  </span>
                </div>
              </SdfTooltip>
            </div>

            <div className="flex flex-col lg:flex-row md:flex-col sm:flex-col mt-4 w-full max-h-auto">
              <div
                className={`flex flex-col items-center p-6 ${section.title === "North American Products" ? 'lg:w-3/5' : ''} ${section.adpMarketplacePartners.bgColor}`}
              >
                <img
                  src={section.adpMarketplacePartners.icon}
                  alt="Us and Canada Icon"
                />
                <span className="font-bold text-heading-04 py-4">
                  {section.adpMarketplacePartners.title}
                </span>
                {section.adpMarketplacePartners.cards.map((card, index) => (
                  <div key={index} className={`flex flex-col`}>
                    <SdfBox
                      key={index}
                      variant="shadowed"
                      className={`${section.commonStyle} ${card.style} ${card.bgColor} flex flex-col justify-between p-6`}
                    >
                      <div className="">
                        <div className="text-heading-03 font-bold pb-3">
                          {card.title}
                        </div>
                        <div className="font-normal leading-md">
                          {card.desc}
                        </div>
                      </div>

                      <div className="lg:mt-8 md:mt-8 mt-2">
                        <SdfButton
                          emphasis="primary"
                          className=""
                          onClick={() => onHandleGetHelp(card.href)}
                        >
                          {card.buttonText || "Get help"}
                        </SdfButton>
                      </div>
                    </SdfBox>
                    <div className="mt-2">
                      {card.bottomText && card.bottomText}
                    </div>
                  </div>
                ))}
              </div>
              <div
                className={`flex flex-col items-center p-6 ${section.title === "North American Products" ? 'lg:w-full' : ''} ${section.adpClients.bgColor} `}
              >
                <img src={section.adpClients.icon} alt="Us and Canada Icon" />
                <span className="font-bold text-heading-04 py-4">
                  {section.adpClients.title}
                </span>
                <div className="flex flex-col lg:flex-row md:flex-row gap-3">
                  {section.adpClients.cards.map((card, index) => (
                    <div key={index} className={`flex flex-col`}>
                      <SdfBox
                        key={index}
                        variant="shadowed"
                        className={`${section.commonStyle} ${card.style} ${card.bgColor} flex flex-col justify-between p-6`}
                      >
                        <div className="">
                          <div className="text-heading-03 font-bold pb-3">
                            {card.title}
                          </div>
                          <div className="font-normal leading-md ">
                            {card.desc}
                          </div>
                        </div>

                        <div className="lg:mt-8 md:mt-8 mt-2">
                          <SdfButton
                            emphasis="primary"
                            className=""
                            onClick={() => onHandleGetHelp(card.href)}
                          >
                             {card.buttonText || "Get help"}
                          </SdfButton>
                        </div>
                      </SdfBox>
                      <div className="mt-2 text-tertiary">
                        {card.bottomText && card.bottomText}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </SdfExpandableBox>
        );
      })}
    </div>
  );
}

export default HelpCenterPage;
